import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Select, SidePanel, type BadgeVariant } from '@silverfalls/sterling';
import { FilterInput } from '../components/FilterInput.js';
import { useToast } from '../components/Toast.js';
import { formatRelativeDate } from '../lib/format.js';
import { sortBy, toggleSort, type SortState } from '../lib/sort.js';
import {
  fetchApInvoices,
  fetchVendors,
  submitCreditNote,
  submitNonPOInvoice,
  type APInvoiceIntakeChannel,
  type APInvoiceRow,
  type APInvoiceState,
  type SubmitCreditNoteInput,
  type SubmitCreditNoteLineInput,
  type SubmitNonPOInvoiceInput,
  type SubmitNonPOInvoiceLineInput,
  type VendorRow,
} from '../api/p2p.js';

const REQUESTING_USER = 'u-ui-skeleton';

const INTAKE_CHANNELS: ReadonlyArray<APInvoiceIntakeChannel> = [
  'MANUAL_ENTRY',
  'EMAIL',
  'VENDOR_PORTAL',
  'EDI',
  'AI_EXTRACTION',
];

type DialogKind = 'NON_PO' | 'CREDIT_NOTE';
type DialogState = { open: false } | { open: true; kind: DialogKind };

type InvoiceSortKey =
  | 'invoiceNumber'
  | 'vendor'
  | 'state'
  | 'total'
  | 'invoiceDate'
  | 'createdAt';

function invoiceSortAccessor(inv: APInvoiceRow, key: InvoiceSortKey): unknown {
  switch (key) {
    case 'invoiceNumber':
      return inv.invoiceNumber;
    case 'vendor':
      return inv.vendor?.legalName ?? inv.vendorRef;
    case 'state':
      return inv.state;
    case 'total':
      return Number(inv.total);
    case 'invoiceDate':
      return inv.invoiceDate;
    case 'createdAt':
      return inv.createdAt;
  }
}

function matchesQuery(inv: APInvoiceRow, q: string): boolean {
  if (q === '') return true;
  const needle = q.toLowerCase();
  return (
    inv.invoiceNumber.toLowerCase().includes(needle) ||
    inv.invoiceRef.toLowerCase().includes(needle) ||
    inv.vendorRef.toLowerCase().includes(needle) ||
    (inv.vendor?.legalName.toLowerCase().includes(needle) ?? false) ||
    inv.state.toLowerCase().includes(needle)
  );
}

const STATE_FILTERS: ReadonlyArray<{ label: string; value: APInvoiceState | 'ALL' }> = [
  { label: 'All', value: 'ALL' },
  { label: 'Variance review', value: 'VARIANCE_REVIEW' },
  { label: 'Matched', value: 'MATCHED' },
  { label: 'On hold', value: 'ON_HOLD' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Rejected', value: 'REJECTED' },
];

interface APInvoicesProps {
  readonly opcoRef: string;
}

export function APInvoices({ opcoRef }: APInvoicesProps): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [invoices, setInvoices] = useState<ReadonlyArray<APInvoiceRow>>([]);
  const [dialog, setDialog] = useState<DialogState>({ open: false });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<APInvoiceState | 'ALL'>('ALL');
  const [kindFilter, setKindFilter] = useState<APInvoiceRow['kind'] | 'ALL'>('ALL');
  const [sort, setSort] = useState<SortState<InvoiceSortKey> | null>({
    key: 'createdAt',
    direction: 'desc',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await fetchApInvoices(opcoRef);
      setInvoices(rows);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [opcoRef]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => {
    let rows = invoices.filter((inv) => matchesQuery(inv, query));
    if (stateFilter !== 'ALL') {
      rows = rows.filter((inv) => inv.state === stateFilter);
    }
    if (kindFilter !== 'ALL') {
      rows = rows.filter((inv) => inv.kind === kindFilter);
    }
    return sortBy(rows, sort, invoiceSortAccessor);
  }, [invoices, query, stateFilter, kindFilter, sort]);

  const onSort = (key: InvoiceSortKey) => setSort((prev) => toggleSort(prev, key));

  const varianceCount = invoices.filter((i) => i.state === 'VARIANCE_REVIEW').length;

  const onExportCsv = useCallback(() => {
    // Export the currently-visible filtered + sorted view so users can pre-narrow.
    exportInvoicesToCsv(visible, opcoRef);
  }, [visible, opcoRef]);

  return (
    <section className="vendors-screen">
      <div className="screen-header">
        <div>
          <h2>AP invoices</h2>
          <p className="screen-header-desc">
            {invoices.length} {invoices.length === 1 ? 'invoice' : 'invoices'}
            {varianceCount > 0 && ` · ${varianceCount} in variance review`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="secondary"
            icon="download"
            onClick={onExportCsv}
            disabled={visible.length === 0}
            title={
              visible.length === 0
                ? 'No rows to export'
                : `Export ${visible.length} row(s) matching the current filter`
            }
          >
            Export CSV
          </Button>
          <Button
            variant="secondary"
            icon="file-text"
            onClick={() => setDialog({ open: true, kind: 'CREDIT_NOTE' })}
            title="Submit a vendor credit note (refund / correction)"
          >
            Submit credit note
          </Button>
          <Button
            variant="primary"
            icon="plus"
            onClick={() => setDialog({ open: true, kind: 'NON_PO' })}
            title="Submit a non-PO invoice (utility, rent, one-off service)"
          >
            Submit non-PO invoice
          </Button>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-neg" role="alert">
          Couldn't load invoices: {loadError}
        </div>
      )}

      {loading ? (
        <div className="t-body-sm">Loading…</div>
      ) : invoices.length === 0 ? (
        <div className="empty-state">
          <p>No AP invoices in {opcoRef} yet. Submit one from a PO's detail page.</p>
        </div>
      ) : (
        <>
          <div className="list-toolbar">
            <FilterInput
              value={query}
              onChange={setQuery}
              placeholder="Search by number, ref, vendor, or state…"
              ariaLabel="Search invoices"
            />
            <Select
              value={stateFilter}
              onValueChange={(v) => setStateFilter(v as APInvoiceState | 'ALL')}
              aria-label="State filter"
              options={STATE_FILTERS.map((f) => ({ value: f.value, label: f.label }))}
            />
            <Select
              value={kindFilter}
              onValueChange={(v) =>
                setKindFilter(v as APInvoiceRow['kind'] | 'ALL')
              }
              aria-label="Kind filter"
              options={[
                { value: 'ALL', label: 'All kinds' },
                { value: 'PO_MATCHED', label: 'PO-matched' },
                { value: 'NON_PO', label: 'Non-PO' },
                { value: 'CREDIT_NOTE', label: 'Credit notes' },
              ]}
            />
            <span className="list-count t-body-sm">
              {visible.length} of {invoices.length}
            </span>
          </div>

          {visible.length === 0 ? (
            <div className="empty-state">
              <p>No invoices match.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader sort={sort} columnKey="invoiceNumber" onSort={onSort}>
                    Invoice #
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="vendor" onSort={onSort}>
                    Vendor
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="state" onSort={onSort}>
                    State
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="createdAt" onSort={onSort}>
                    Submitted
                  </SortHeader>
                  <th>Kind</th>
                  <SortHeader sort={sort} columnKey="total" onSort={onSort}>
                    Total
                  </SortHeader>
                </tr>
              </thead>
              <tbody>
                {visible.map((inv) => (
                  <tr
                    key={inv.invoiceRef}
                    className="row-clickable"
                    onClick={() => navigate(`/ap-invoices/${inv.invoiceRef}`)}
                  >
                    <td className="cell-primary">{inv.invoiceNumber}</td>
                    <td>{inv.vendor?.legalName ?? '—'}</td>
                    <td>
                      <Badge variant={stateBadgeVariant(inv.state)} dot={false}>
                        {inv.state}
                      </Badge>
                    </td>
                    <td>{formatRelativeDate(inv.createdAt)}</td>
                    <td>
                      <Badge variant={kindBadgeVariant(inv.kind)} dot={false}>
                        {kindBadgeLabel(inv.kind)}
                      </Badge>
                    </td>
                    <td className="mono">
                      {inv.total} {inv.currencyCode}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {dialog.open && (
        <NewAPInvoiceDialog
          opcoRef={opcoRef}
          kind={dialog.kind}
          existingInvoices={invoices}
          onClose={() => setDialog({ open: false })}
          onCreated={(invoiceRef) => {
            setDialog({ open: false });
            toast.push(
              dialog.kind === 'CREDIT_NOTE'
                ? `Credit note submitted (${invoiceRef})`
                : `Non-PO invoice submitted (${invoiceRef})`,
              'success',
            );
            void load();
          }}
          onError={(message) => toast.push(message, 'error')}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// New AP invoice dialog (handles non-PO invoices and credit notes)
// ---------------------------------------------------------------------------

interface NewAPInvoiceDialogProps {
  readonly opcoRef: string;
  readonly kind: DialogKind;
  readonly existingInvoices: ReadonlyArray<APInvoiceRow>;
  readonly onClose: () => void;
  readonly onCreated: (invoiceRef: string) => void;
  readonly onError: (message: string) => void;
}

interface DraftAPLine {
  readonly id: string;
  description: string;
  quantity: string;
  unitPrice: string;
  glAccount: string;
}

function emptyAPLine(): DraftAPLine {
  return {
    id: crypto.randomUUID(),
    description: '',
    quantity: '1',
    unitPrice: '0',
    glAccount: '6100-MRO',
  };
}

function NewAPInvoiceDialog({
  opcoRef,
  kind,
  existingInvoices,
  onClose,
  onCreated,
  onError,
}: NewAPInvoiceDialogProps): JSX.Element {
  const [vendors, setVendors] = useState<ReadonlyArray<VendorRow>>([]);
  const [vendorLoadError, setVendorLoadError] = useState<string | null>(null);
  const [vendorRef, setVendorRef] = useState<string>('');
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [invoiceDate, setInvoiceDate] = useState<string>(
    new Date().toISOString().slice(0, 10),
  );
  const [currencyCode, setCurrencyCode] = useState<string>('USD');
  const [intakeChannel, setIntakeChannel] =
    useState<APInvoiceIntakeChannel>('MANUAL_ENTRY');
  const [notes, setNotes] = useState<string>('');
  const [sourceInvoiceRef, setSourceInvoiceRef] = useState<string>('');
  const [lines, setLines] = useState<ReadonlyArray<DraftAPLine>>([emptyAPLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const isCreditNote = kind === 'CREDIT_NOTE';

  // For credit notes, only allow against invoices for the selected vendor that
  // are MATCHED/APPROVED (not REJECTED/CANCELLED). The gadget enforces vendor
  // match, but pre-filtering keeps the UX honest.
  const eligibleSourceInvoices = useMemo(() => {
    if (!isCreditNote || vendorRef === '') return [];
    return existingInvoices
      .filter((inv) => inv.vendorRef === vendorRef)
      .filter((inv) => inv.kind !== 'CREDIT_NOTE')
      .filter(
        (inv) =>
          inv.state === 'MATCHED' ||
          inv.state === 'APPROVED' ||
          inv.state === 'VARIANCE_REVIEW',
      );
  }, [isCreditNote, vendorRef, existingInvoices]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await fetchVendors(opcoRef);
        if (cancelled) return;
        const active = rows.filter((v) => v.state === 'ACTIVE');
        setVendors(active);
        if (active.length > 0 && active[0]) setVendorRef(active[0].vendorRef);
      } catch (err) {
        if (cancelled) return;
        setVendorLoadError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opcoRef]);

  const updateLine = (id: string, patch: Partial<DraftAPLine>) =>
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, emptyAPLine()]);
  const removeLine = (id: string) =>
    setLines((prev) => (prev.length === 1 ? prev : prev.filter((l) => l.id !== id)));

  const subtotal = lines.reduce((sum, l) => {
    const q = Number(l.quantity);
    const p = Number(l.unitPrice);
    if (!Number.isFinite(q) || !Number.isFinite(p)) return sum;
    return sum + q * p;
  }, 0);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    if (!vendorRef) {
      setError('Select a vendor');
      setSubmitting(false);
      return;
    }
    if (invoiceNumber.trim() === '') {
      setError('Invoice number is required');
      setSubmitting(false);
      return;
    }

    try {
      if (isCreditNote) {
        const apiLines: SubmitCreditNoteLineInput[] = lines.map((l) => ({
          description: l.description.trim(),
          quantity: l.quantity.trim(),
          unitPrice: l.unitPrice.trim(),
          glAccount: l.glAccount.trim(),
        }));
        const cnInput: SubmitCreditNoteInput = {
          vendorRef,
          opcoRef,
          invoiceNumber: invoiceNumber.trim(),
          invoiceDate: new Date(invoiceDate).toISOString(),
          currencyCode: currencyCode.trim().toUpperCase(),
          lines: apiLines,
          intakeChannel,
          ...(notes.trim() !== '' ? { notes: notes.trim() } : {}),
          ...(sourceInvoiceRef !== '' ? { sourceInvoiceRef } : {}),
          requestingUser: REQUESTING_USER,
        };
        const result = await submitCreditNote(cnInput);
        if (result.__typename === 'SubmitCreditNoteFailure') {
          const msg = `${result.class}: ${JSON.stringify(result.details)}`;
          setError(msg);
          onError(msg);
          setSubmitting(false);
          return;
        }
        onCreated(result.invoiceRef);
        return;
      }

      const apiLines: SubmitNonPOInvoiceLineInput[] = lines.map((l) => ({
        description: l.description.trim(),
        quantity: l.quantity.trim(),
        unitPrice: l.unitPrice.trim(),
        glAccount: l.glAccount.trim(),
      }));
      const input: SubmitNonPOInvoiceInput = {
        vendorRef,
        opcoRef,
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate: new Date(invoiceDate).toISOString(),
        currencyCode: currencyCode.trim().toUpperCase(),
        lines: apiLines,
        intakeChannel,
        ...(notes.trim() !== '' ? { notes: notes.trim() } : {}),
        requestingUser: REQUESTING_USER,
      };
      const result = await submitNonPOInvoice(input);
      if (result.__typename === 'SubmitNonPOInvoiceFailure') {
        const msg = `${result.class}: ${JSON.stringify(result.details)}`;
        setError(msg);
        onError(msg);
        setSubmitting(false);
        return;
      }
      onCreated(result.invoiceRef);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      onError(msg);
      setSubmitting(false);
    }
  };

  return (
    <SidePanel
      open
      title={isCreditNote ? 'Submit credit note' : 'Submit non-PO invoice'}
      width={640}
      onClose={onClose}
      onSave={() => formRef.current?.requestSubmit()}
      dirty={!submitting && vendors.length > 0}
      saveLabel={
        submitting
          ? 'Submitting…'
          : isCreditNote
            ? 'Submit credit note'
            : 'Submit invoice'
      }
    >
      <form ref={formRef} onSubmit={onSubmit}>
          {vendorLoadError && (
            <div className="alert alert-neg" role="alert">
              Couldn't load vendors: {vendorLoadError}
            </div>
          )}

          <div className="form-grid">
            <label className="form-row">
              <span className="form-label">Vendor (active only)</span>
              <select
                value={vendorRef}
                onChange={(e) => {
                  setVendorRef(e.target.value);
                  setSourceInvoiceRef('');
                }}
                required
                disabled={vendors.length === 0}
              >
                {vendors.length === 0 && <option value="">No active vendors</option>}
                {vendors.map((v) => (
                  <option key={v.vendorRef} value={v.vendorRef}>
                    {v.legalName}
                  </option>
                ))}
              </select>
            </label>
            {isCreditNote && (
              <label className="form-row">
                <span className="form-label">Against invoice (optional)</span>
                <select
                  value={sourceInvoiceRef}
                  onChange={(e) => setSourceInvoiceRef(e.target.value)}
                  disabled={eligibleSourceInvoices.length === 0}
                >
                  <option value="">— Standalone credit —</option>
                  {eligibleSourceInvoices.map((inv) => (
                    <option key={inv.invoiceRef} value={inv.invoiceRef}>
                      {inv.invoiceNumber} ({inv.total} {inv.currencyCode}, {inv.state})
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="form-row">
              <span className="form-label">
                {isCreditNote ? 'Credit note #' : 'Invoice number'}
              </span>
              <input
                type="text"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                required
                placeholder={
                  isCreditNote ? 'e.g. CN-2026-05-001' : 'e.g. INV-2026-05-001'
                }
              />
            </label>
            <label className="form-row">
              <span className="form-label">
                {isCreditNote ? 'Credit note date' : 'Invoice date'}
              </span>
              <input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                required
              />
            </label>
            <label className="form-row">
              <span className="form-label">Currency</span>
              <input
                type="text"
                value={currencyCode}
                onChange={(e) => setCurrencyCode(e.target.value)}
                required
                maxLength={3}
              />
            </label>
            <label className="form-row">
              <span className="form-label">Intake channel</span>
              <select
                value={intakeChannel}
                onChange={(e) =>
                  setIntakeChannel(e.target.value as APInvoiceIntakeChannel)
                }
              >
                {INTAKE_CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-row form-row-full">
              <span className="form-label">Notes (optional)</span>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Internal notes"
              />
            </label>
          </div>

          <div className="po-lines">
            <div className="po-lines-header">
              <span className="form-label">Lines</span>
              <button type="button" className="btn btn-sm" onClick={addLine}>
                + Add line
              </button>
            </div>
            <table className="po-lines-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Unit price</th>
                  <th>GL</th>
                  <th>Line total</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => {
                  const q = Number(l.quantity);
                  const p = Number(l.unitPrice);
                  const total =
                    Number.isFinite(q) && Number.isFinite(p) ? (q * p).toFixed(4) : '—';
                  return (
                    <tr key={l.id}>
                      <td className="mono">{idx + 1}</td>
                      <td>
                        <input
                          type="text"
                          value={l.description}
                          onChange={(e) =>
                            updateLine(l.id, { description: e.target.value })
                          }
                          required
                          placeholder="May electricity"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={l.quantity}
                          onChange={(e) => updateLine(l.id, { quantity: e.target.value })}
                          required
                          className="po-line-num"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={l.unitPrice}
                          onChange={(e) =>
                            updateLine(l.id, { unitPrice: e.target.value })
                          }
                          required
                          className="po-line-num"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={l.glAccount}
                          onChange={(e) =>
                            updateLine(l.id, { glAccount: e.target.value })
                          }
                          required
                          className="po-line-gl"
                          placeholder="6200-UTIL"
                        />
                      </td>
                      <td className="mono">{total}</td>
                      <td>
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => removeLine(l.id)}
                          aria-label="Remove line"
                          disabled={lines.length === 1}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} className="po-lines-foot-label">
                    Total
                  </td>
                  <td className="mono">{subtotal.toFixed(4)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          {error && (
            <div className="alert alert-neg" role="alert">
              {error}
            </div>
          )}

      </form>
    </SidePanel>
  );
}

function exportInvoicesToCsv(rows: ReadonlyArray<APInvoiceRow>, opcoRef: string): void {
  const header = [
    'invoiceRef',
    'invoiceNumber',
    'vendorRef',
    'vendorLegalName',
    'opcoRef',
    'state',
    'kind',
    'intakeChannel',
    'currencyCode',
    'subtotal',
    'total',
    'invoiceDate',
    'submittedAt',
    'submittedBy',
    'lineCount',
    'varianceSummary',
    'notes',
  ];
  const escape = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const lines = rows.map((inv) => {
    const varianceLines = inv.lines.filter((l) => l.variance.kind !== 'NONE').length;
    const varianceSummary =
      varianceLines === 0
        ? 'NONE'
        : `${varianceLines}/${inv.lines.length} lines variant`;
    return [
      inv.invoiceRef,
      inv.invoiceNumber,
      inv.vendorRef,
      inv.vendor?.legalName ?? '',
      inv.opcoRef,
      inv.state,
      inv.kind,
      inv.intakeChannel,
      inv.currencyCode,
      inv.subtotal,
      inv.total,
      inv.invoiceDate,
      inv.createdAt,
      inv.createdBy,
      String(inv.lines.length),
      varianceSummary,
      inv.notes ?? '',
    ]
      .map(escape)
      .join(',');
  });
  const blob = new Blob([[header.join(','), ...lines].join('\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  a.download = `anvil-ap-invoices-${opcoRef}-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function kindBadgeVariant(kind: APInvoiceRow['kind']): BadgeVariant {
  switch (kind) {
    case 'PO_MATCHED':
      return 'neutral';
    case 'NON_PO':
      return 'info';
    case 'CREDIT_NOTE':
      return 'warning';
  }
}

function kindBadgeLabel(kind: APInvoiceRow['kind']): string {
  switch (kind) {
    case 'PO_MATCHED':
      return 'PO';
    case 'NON_PO':
      return 'NON_PO';
    case 'CREDIT_NOTE':
      return 'CREDIT';
  }
}

function stateBadgeVariant(state: APInvoiceState): BadgeVariant {
  switch (state) {
    case 'MATCHED':
    case 'APPROVED':
      return 'success';
    case 'RECEIVED':
      return 'info';
    case 'VARIANCE_REVIEW':
    case 'ON_HOLD':
      return 'warning';
    case 'REJECTED':
    case 'CANCELLED':
      return 'neutral';
  }
}

interface SortHeaderProps {
  readonly sort: SortState<InvoiceSortKey> | null;
  readonly columnKey: InvoiceSortKey;
  readonly onSort: (key: InvoiceSortKey) => void;
  readonly children: JSX.Element | string;
}

function SortHeader({ sort, columnKey, onSort, children }: SortHeaderProps): JSX.Element {
  const active = sort?.key === columnKey;
  const indicator = active ? (sort?.direction === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th>
      <button
        type="button"
        className={`th-sort${active ? ' th-sort-active' : ''}`}
        onClick={() => onSort(columnKey)}
      >
        {children}
        <span className="th-sort-indicator" aria-hidden>
          {indicator}
        </span>
      </button>
    </th>
  );
}
